import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { supabase } from '@/api/supabaseClient';
import { sendMetaEvent } from '../api/metaCapi';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import GoogleIcon from '@/components/icons/GoogleIcon';
import UgcFacebookReel from '@/components/marketing/UgcFacebookReel';
import { CheckCircle2, Eye, EyeOff, XCircle } from 'lucide-react';
import { notifySuccess, notifyError } from '@/lib/notify';
import { trackEvent } from '@/api/trackEvent';
import { pushGtmEvent } from '@/lib/gtm';
import { captureReferralFromSearch, applyStoredReferral } from '@/utils/referral';

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getSiteOrigin() {
  // During prerender (Node), `window` is not defined.
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  const env =
    (import.meta?.env && (import.meta.env.VITE_SITE_URL || import.meta.env.VITE_PUBLIC_SITE_URL)) || '';
  if (env) return String(env).replace(/\/$/, '');
  return 'https://tresopilot.fr';
}

function buildRedirectTo(nextUrl) {
  // Supabase requires absolute redirect URLs.
  const origin = getSiteOrigin();
  return `${origin}${createPageUrl('AuthCallback')}?next=${encodeURIComponent(nextUrl)}`;
}

export default function Auth({ defaultTab = 'login' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();

  // Capture referral code from URL and keep it for after signup/OAuth.
  useEffect(() => {
    captureReferralFromSearch(location?.search || '');
  }, [location?.search]);

  // Capture ?ref=CODE from referral links and keep it for after signup/OAuth.
  useEffect(() => {
    captureReferralFromSearch(location?.search || '');
  }, [location?.search]);

  // Used to display reassurance/benefits copy on all /register URLs (including trailing slash or query params).
  const isRegisterRoute = (location?.pathname || '').toLowerCase().includes('/register');

  // We keep the existing behavior: next is a PATH (e.g. /dashboard)
  const nextUrl = params.get('next') || createPageUrl('Dashboard');
  const redirectTo = buildRedirectTo(nextUrl);

  const [tab, setTab] = useState(defaultTab);
  const showRegisterMarketing = tab === 'register' || isRegisterRoute;

  // Shared state (so switching tabs keeps what the user already typed)
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // UI helpers
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirm, setShowRegisterConfirm] = useState(false);

  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [showConfirmHint, setShowConfirmHint] = useState(false);

  const setActiveTab = (value) => {
    setTab(value);
    setLoginError('');
    setRegisterError('');
    setShowConfirmHint(false);

    // Keep existing public links working (/login and /register) while serving a single UI.
    // When user switches tabs, we update the route but preserve query params.
    const path = value === 'register' ? createPageUrl('Register') : createPageUrl('Login');
    const sp = new URLSearchParams(location.search);
    if (email) sp.set('email', email);
    navigate(`${path}?${sp.toString()}`, { replace: true });
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (loginError) setLoginError('');
    if (registerError) setRegisterError('');
    if (showConfirmHint) setShowConfirmHint(false);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (loginError) setLoginError('');
    if (registerError) setRegisterError('');
  };

  const handleConfirmChange = (e) => {
    setConfirm(e.target.value);
    if (registerError) setRegisterError('');
  };

  const passwordChecks = useMemo(() => {
    const pwd = password || '';
    const hasMinLength = pwd.length >= 8;
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasDigit = /[0-9]/.test(pwd);
    // Matches any of: !@#$%^&*()_+-=[]{};':"|<>?,./`~ and backslash
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"|\\<>?,.\/`~]/.test(pwd);
    return {
      hasMinLength,
      hasLower,
      hasUpper,
      hasDigit,
      hasSpecial,
      isValid: hasMinLength && hasLower && hasUpper && hasDigit && hasSpecial,
    };
  }, [password]);

  const passwordRuleItems = useMemo(
    () => [
      { ok: passwordChecks.hasMinLength, label: '8 caractères minimum' },
      { ok: passwordChecks.hasLower, label: '1 minuscule (a-z)' },
      { ok: passwordChecks.hasUpper, label: '1 majuscule (A-Z)' },
      { ok: passwordChecks.hasDigit, label: '1 chiffre (0-9)' },
      { ok: passwordChecks.hasSpecial, label: '1 caractère spécial (!@#…)' },
    ],
    [passwordChecks],
  );

  const handleGoogle = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            prompt: 'select_account',
          },
        },
      });
      if (error) throw error;
// Redirect happens automatically.
    } catch (err) {
      notifyError(err?.message || 'Connexion Google impossible.');
      setIsGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowConfirmHint(false);
    setLoginError('');
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
navigate(nextUrl);
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      if (msg.includes('email not confirmed') || msg.includes('non confirmé') || msg.includes('not confirmed')) {
        setShowConfirmHint(true);
        notifyError('Compte non confirmé : renvoyez l’email de confirmation puis cliquez sur le lien reçu.');
      } else if (
        msg.includes('invalid login credentials') ||
        (msg.includes('invalid') && msg.includes('credentials')) ||
        msg.includes('invalid email or password')
      ) {
        setLoginError('Email ou mot de passe incorrect.');
      } else {
        notifyError(err?.message || 'Impossible de se connecter.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    if (!passwordChecks.isValid) {
      setRegisterError(
        'Mot de passe trop faible : ajoutez au moins 1 minuscule, 1 majuscule, 1 chiffre et 1 caractère spécial (8 caractères minimum).',
      );
      return;
    }
    if (password !== confirm) {
      setRegisterError('Les mots de passe ne correspondent pas.');
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
        },
      });
      if (error) throw error;

      // Google Ads (via GTM): SignUp conversion (tp_signup)
      try {
        const uid = data?.user?.id || null;
        pushGtmEvent('tp_signup', { method: 'email_password' }, { onceKey: `tp_signup:${uid || email}` });
      } catch (_) {}

      // Meta Pixel + CAPI: CompleteRegistration (registration only, not login/reset/resend)
      try {
        const externalId = data?.user?.id;
        await sendMetaEvent({
          eventName: 'CompleteRegistration',
          email,
          externalId,
          customData: { content_name: 'Signup', status: data?.session ? 'created_and_logged_in' : 'created' },
          eventSourceUrl: window.location.href,
        });

        // Avoid duplicate firing later in AuthCallback (email link / OAuth).
        if (externalId && typeof localStorage !== 'undefined') {
          localStorage.setItem(`tresopilot_meta_cr_sent_${externalId}`, '1');
          // Internal first-party tracking (Supabase): complete_registration
          try {
            if (externalId && typeof localStorage !== 'undefined') {
              const k = `tresopilot_track_cr_sent_${externalId}`;
              if (!localStorage.getItem(k)) {
                trackEvent({
                  eventName: 'complete_registration',
                  page: window.location.href,
                  source: 'auth',
                  userId: externalId,
                  customData: { method: 'email_password', status: data?.session ? 'created_and_logged_in' : 'created' },
                });
                localStorage.setItem(k, '1');
              }
            } else {
              trackEvent({
                eventName: 'complete_registration',
                page: window.location.href,
                source: 'auth',
                customData: { method: 'email_password', status: data?.session ? 'created_and_logged_in' : 'created' },
              });
            }
          } catch (_) {}

        }
      } catch (_) {}
// If email confirmation is enabled, session can be null here.
      if (!data?.session) {
        notifySuccess('Compte créé ! Vérifiez votre email pour confirmer votre inscription.');
        navigate(createPageUrl('CheckEmail') + `?email=${encodeURIComponent(email)}&next=${encodeURIComponent(nextUrl)}`);
        return;
      }

      notifySuccess('Compte créé. Bienvenue !');

      // Apply referral if the user arrived via ?ref=CODE (idempotent server-side).
      try {
        const uid = data?.session?.user?.id;
        if (uid) await applyStoredReferral({ supabase, userId: uid });
      } catch (_) {}

      navigate(nextUrl);
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      if (msg.includes('password') && (msg.includes('should contain') || msg.includes('weak') || msg.includes('strength'))) {
        setRegisterError(
          'Mot de passe trop faible : ajoutez au moins 1 minuscule, 1 majuscule, 1 chiffre et 1 caractère spécial (8 caractères minimum).',
        );
      } else {
        notifyError(err?.message || 'Impossible de créer le compte.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      notifyError('Veuillez saisir votre email.');
      return;
    }
    setIsResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${createPageUrl('ResetPassword')}`,
      });
      if (error) throw error;
notifySuccess('Email de réinitialisation envoyé.');
    } catch (err) {
      notifyError(err?.message || 'Échec de l’envoi de l’email.');
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleResendConfirm = async () => {
    if (!email) {
      notifyError('Veuillez saisir votre email.');
      return;
    }
    setIsResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: redirectTo,
        },
      });
      if (error) throw error;
notifySuccess('Email de confirmation renvoyé.');
    } catch (err) {
      notifyError(err?.message || 'Impossible de renvoyer l’email.');
    } finally {
      setIsResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center justify-center mb-6 gap-2">
          <BrandLink />
          <Tagline className="text-slate-500 text-sm" />
        </div>

        <div className="mb-4">
          <Button variant="ghost" className="px-0" asChild>
            <Link to="/">← Retour à l'accueil</Link>
          </Button>
        </div>

        <Card className="border-slate-200">
          <Tabs value={tab} onValueChange={setActiveTab} className="w-full">
            <CardHeader className="space-y-3">
              <div>
                <CardTitle>{tab === 'register' ? 'Créer un compte' : 'Se connecter'}</CardTitle>
                <CardDescription>
                  {tab === 'register'
                    ? "Inscription gratuite, en 1 minute."
                    : 'Accédez à votre tableau de bord TrésoPilot.'}
                </CardDescription>
              </div>

              {showRegisterMarketing && tab === 'register' && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm text-slate-900">
                    <span className="block font-semibold">Vous encaissez. TrésoPilot calcule.</span>
                    <span className="mt-1 block font-medium">
                      Vous savez quoi mettre de côté (URSSAF/TVA) — et ce qu’il vous reste vraiment.
                    </span>
                  </p>

                  <ul className="mt-3 list-disc space-y-2 pl-5 text-xs text-slate-800 marker:text-emerald-600">
                    <li>Échéancier URSSAF/TVA automatique (selon votre profil)</li>
                    <li>“Vrai net” + taxes estimées en un tableau clair</li>
                    <li>Rappels in-app gratuits pour ne rien oublier</li>
                    <li>Créez votre compte gratuit et voyez vos chiffres immédiatement.</li>
                  </ul>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full border border-emerald-200 bg-white/70 px-2 py-1 text-[11px] font-medium text-emerald-800">
                      Gratuit pour commencer
                    </span>
                    <span className="rounded-full border border-emerald-200 bg-white/70 px-2 py-1 text-[11px] font-medium text-emerald-800">
                      Sans carte bancaire
                    </span>
                    <span className="rounded-full border border-emerald-200 bg-white/70 px-2 py-1 text-[11px] font-medium text-emerald-800">
                      Configuration guidée
                    </span>
                  </div>

                  <p className="mt-3 text-[11px] font-medium text-slate-700">
                    🔒 Données chiffrées et hébergées en Europe
                  </p>

                  <div className="mt-4 flex flex-col items-center gap-2">
                    <div className="text-xs font-semibold text-slate-800">Retour d’expérience (vidéo)</div>
                    <UgcFacebookReel className="max-w-[260px]" />
                  </div>
                </div>
              )}

              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Se connecter</TabsTrigger>
                <TabsTrigger value="register">Créer un compte</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="space-y-4">
              <Button type="button" variant="outline" className="w-full" onClick={handleGoogle} disabled={isGoogleLoading}>
                <GoogleIcon className="mr-2 h-4 w-4" />
                Continuer avec Google
              </Button>

              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-slate-500">ou</span>
                <Separator className="flex-1" />
              </div>

              <TabsContent value="login" className="mt-0">
                  {loginError && (
                    <Alert className="border-rose-200 bg-rose-50 mb-3">
                      <AlertDescription className="text-sm text-rose-900">
                        {loginError}
                      </AlertDescription>
                    </Alert>
                  )}
                  <form onSubmit={handleLogin} className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={email}
                        onChange={handleEmailChange}
                        autoComplete="email"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="password">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showLoginPassword ? 'text' : 'password'}
                          placeholder="Votre mot de passe"
                          value={password}
                          onChange={handlePasswordChange}
                          autoComplete="current-password"
                          className="pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword((v) => !v)}
                          aria-label={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Connexion…' : 'Se connecter'}
                    </Button>
                  </form>

                  <div className="flex flex-col gap-2 mt-3">
                    {showConfirmHint && (
                      <Alert className="border-amber-200 bg-amber-50">
                        <AlertDescription className="text-sm text-amber-900">
                          Votre compte n’est pas confirmé. Renvoyez l’email de confirmation puis cliquez sur le lien reçu.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button type="button" variant="link" className="px-0 justify-start" onClick={handleResetPassword} disabled={isResetLoading}>
                      {isResetLoading ? 'Envoi…' : 'Mot de passe oublié ?'}
                    </Button>
                    <Button type="button" variant="link" className="px-0 justify-start" onClick={handleResendConfirm} disabled={isResendLoading}>
                      {isResendLoading ? 'Envoi…' : 'Renvoyer l’email de confirmation'}
                    </Button>
                  </div>
                </TabsContent>

              <TabsContent value="register" className="mt-0">
                  {registerError && (
                    <Alert className="border-rose-200 bg-rose-50 mb-3">
                      <AlertDescription className="text-sm text-rose-900">
                        {registerError}
                      </AlertDescription>
                    </Alert>
                  )}
                  <form onSubmit={handleRegister} className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="email-register">Email</Label>
                      <Input
                        id="email-register"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={email}
                        onChange={handleEmailChange}
                        autoComplete="email"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="password-register">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="password-register"
                          type={showRegisterPassword ? 'text' : 'password'}
                          placeholder="8 caractères minimum"
                          value={password}
                          onChange={handlePasswordChange}
                          autoComplete="new-password"
                          className="pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword((v) => !v)}
                          aria-label={showRegisterPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>

                      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs font-semibold text-slate-700">Règles du mot de passe</p>
                          <span
                            className={`text-[11px] font-medium ${
                              passwordChecks.isValid ? 'text-emerald-700' : 'text-slate-500'
                            }`}
                          >
                            {passwordChecks.isValid ? 'OK' : 'À compléter'}
                          </span>
                        </div>

                        <ul className="mt-2 space-y-1">
                          {passwordRuleItems.map((it) => (
                            <li key={it.label} className="flex items-center gap-2">
                              {it.ok ? (
                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                              ) : (
                                <XCircle className="h-5 w-5 text-rose-500" />
                              )}
                              <span className={`text-xs ${it.ok ? 'text-slate-800' : 'text-slate-600'}`}>{it.label}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="confirm">Confirmer le mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="confirm"
                          type={showRegisterConfirm ? 'text' : 'password'}
                          value={confirm}
                          onChange={handleConfirmChange}
                          autoComplete="new-password"
                          className="pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterConfirm((v) => !v)}
                          aria-label={showRegisterConfirm ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          {showRegisterConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Création…' : 'Créer mon compte'}
                    </Button>
                  </form></TabsContent>
            </CardContent>

            <CardFooter className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{tab === 'register' ? 'Déjà un compte ?' : 'Pas de compte ?'}</span>
              <Button
                type="button"
                variant="link"
                className="px-0 text-emerald-600"
                onClick={() => setActiveTab(tab === 'register' ? 'login' : 'register')}
              >
                {tab === 'register' ? 'Se connecter' : 'Créer un compte'}
              </Button>
            </CardFooter>
          </Tabs>
        </Card>

        <div className="text-center mt-6 text-xs text-slate-500">
          En continuant, vous acceptez nos{' '}
          <Link className="text-emerald-600 hover:underline" to={createPageUrl('CGU')}>
            CGU
          </Link>
          .
        </div>
      </div>
    </div>
  );
}